<template>
  <v-main>
    <v-container fluid>
      <v-row justify="center" class="mt-4 ml-4">
        <v-col>
          <span class="text-h5 font-weight-bold" v-text="'User Locations'"></span>
          <div class="grey--text text-caption">
            Assign work sites that employees can punch in from. Punch-in coordinates outside every assigned site's radius will be rejected.
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="6">
            <v-card-text>
              <v-row align="center">
                <v-col md="3">
                  <v-text-field
                    v-model="search_keyword"
                    label="Search user..."
                    prepend-inner-icon="mdi-magnify"
                    outlined dense hide-details clearable
                    @keydown.enter="getData"
                    @change="page = 1"
                  ></v-text-field>
                </v-col>
                <v-col md="3" v-if="allCompanies.length > 0">
                  <v-autocomplete
                    v-model="filter_company_id"
                    :items="allCompanies"
                    label="Company"
                    prepend-inner-icon="mdi-domain"
                    outlined dense hide-details clearable
                    @input="onCompanyChange"
                  ></v-autocomplete>
                </v-col>
                <v-col md="2">
                  <v-autocomplete
                    v-model="filter_geolocation_id"
                    :items="geolocationOptions"
                    label="Filter by Location"
                    outlined dense hide-details clearable
                    prepend-inner-icon="mdi-map-marker"
                    @input="getData"
                  ></v-autocomplete>
                </v-col>
                <v-col md="2">
                  <v-checkbox
                    v-model="without_location"
                    label="Without Location"
                    hide-details
                    @change="getData"
                  ></v-checkbox>
                </v-col>
                <v-spacer></v-spacer>
                <v-col md="2">
                  <v-pagination v-model="page" :length="paginationLength"></v-pagination>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Snackbar -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3500" top right>
        {{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
        </template>
      </v-snackbar>

      <!-- Bulk Actions -->
      <v-row v-if="selectedUsers.length > 0">
        <v-col md="12">
          <v-card elevation="6" color="blue lighten-5">
            <v-card-text>
              <v-row align="center">
                <v-col md="6">
                  <v-autocomplete
                    v-model="bulk_geolocation_id"
                    :items="geolocationOptions"
                    label="Select Location for Bulk Assignment"
                    prepend-icon="mdi-map-marker"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col md="6">
                  <v-btn
                    color="primary"
                    @click="assignBulkLocations"
                    :disabled="!bulk_geolocation_id"
                    :loading="isBulkLoading"
                  >
                    Assign Location to {{ selectedUsers.length }} Users
                  </v-btn>
                  <v-btn
                    color="error"
                    class="ml-2"
                    @click="removeBulkLocations"
                    :disabled="!bulk_geolocation_id"
                    :loading="isBulkLoading"
                  >
                    Remove Location from {{ selectedUsers.length }} Users
                  </v-btn>
                  <v-btn text class="ml-2" @click="selectedUsers = []">
                    Clear Selection
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-progress-linear v-if="isLoading" indeterminate color="primary"></v-progress-linear>
      <v-row v-else>
        <v-col md="12">
          <v-card elevation="6">
            <v-simple-table fixed-header>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">
                      <v-checkbox v-model="selectAll" @change="toggleSelectAll"></v-checkbox>
                    </th>
                    <th class="text-left">Sr No</th>
                    <th class="text-left">Name</th>
                    <th class="text-left">Employee Code</th>
                    <th class="text-left">Email</th>
                    <th class="text-left">Designation</th>
                    <th class="text-left">Assigned Locations</th>
                    <th class="text-right">Action</th>
                  </tr>
                </thead>
                <tbody v-if="users.length != 0">
                  <tr v-for="(user, i) in users" :key="`user${i}`">
                    <td>
                      <v-checkbox v-model="selectedUsers" :value="user.id"></v-checkbox>
                    </td>
                    <td>{{ (page - 1) * rowsPerPage + i + 1 }}</td>
                    <td>{{ user.name }}</td>
                    <td>{{ user.employee_code }}</td>
                    <td>{{ user.email }}</td>
                    <td>{{ user.designation ? (user.designation.description || user.designation.name) : "N/A" }}</td>
                    <td>
                      <div v-if="user.geolocations && user.geolocations.length > 0">
                        <v-chip
                          v-for="(loc, idx) in user.geolocations"
                          :key="`loc-${idx}`"
                          small
                          :color="loc.pivot && loc.pivot.is_primary ? 'primary' : 'secondary'"
                          class="ma-1"
                          close
                          @click:close="quickRemove(user, loc)"
                        >
                          {{ loc.location }} ({{ loc.radius || 200 }}m)
                          <v-icon v-if="loc.pivot && loc.pivot.is_primary" small right color="white">
                            mdi-star
                          </v-icon>
                        </v-chip>
                      </div>
                      <v-chip v-else-if="user.geolocation" small color="primary">
                        {{ user.geolocation.location }} ({{ user.geolocation.radius || 200 }}m)
                      </v-chip>
                      <span v-else class="grey--text">No Location Assigned</span>
                    </td>
                    <td class="text-right">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn v-bind="attrs" v-on="on" icon @click="openAssignDialog(user)">
                            <v-icon small>mdi-map-marker-plus</v-icon>
                          </v-btn>
                        </template>
                        <span>Assign Location</span>
                      </v-tooltip>
                      <v-tooltip bottom v-if="(user.geolocations && user.geolocations.length > 0)">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn v-bind="attrs" v-on="on" icon @click="openRemoveDialog(user)">
                            <v-icon small>mdi-map-marker-remove</v-icon>
                          </v-btn>
                        </template>
                        <span>Remove Location(s)</span>
                      </v-tooltip>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="8" class="text-center">No users found</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Assign Location Dialog -->
      <v-dialog v-model="assignDialog" max-width="500">
        <v-card>
          <v-card-title>
            Assign Location to {{ selectedUser.name }}
          </v-card-title>
          <v-card-text>
            <v-form ref="assignForm">
              <v-autocomplete
                v-model="assign_geolocation_id"
                :items="availableLocations"
                label="Select Location *"
                prepend-icon="mdi-map-marker"
                :rules="[rules.required]"
              ></v-autocomplete>
              <v-checkbox
                v-model="assign_is_primary"
                label="Set as Primary Location"
                hint="Primary location is the user's main work site."
                persistent-hint
              ></v-checkbox>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="assignDialog = false">Cancel</v-btn>
            <v-btn
              color="primary"
              @click="assignLocation"
              :loading="isAssignLoading"
              :disabled="!assign_geolocation_id"
            >
              Assign
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Remove Location Dialog -->
      <v-dialog v-model="removeDialog" max-width="500">
        <v-card>
          <v-card-title>
            Remove Location from {{ selectedUser.name }}
          </v-card-title>
          <v-card-text>
            <v-select
              v-model="remove_geolocation_id"
              :items="userLocationsForRemoval"
              label="Select Location to Remove"
              prepend-icon="mdi-map-marker"
              hint="Select a specific location or remove all"
              persistent-hint
            ></v-select>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="removeDialog = false">Cancel</v-btn>
            <v-btn
              color="error"
              @click="removeLocation"
              :loading="isRemoveLoading"
              :disabled="!remove_geolocation_id"
            >
              Remove
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "UserLocationsPage",
  data() {
    return {
      search_keyword: "",
      filter_geolocation_id: null,
      without_location: false,
      page: 1,
      paginationLength: 1,
      users: [],
      geolocations: [],
      isLoading: true,
      assignDialog: false,
      removeDialog: false,
      selectedUser: {},
      assign_geolocation_id: null,
      assign_is_primary: false,
      remove_geolocation_id: null,
      isAssignLoading: false,
      isRemoveLoading: false,
      selectedUsers: [],
      selectAll: false,
      bulk_geolocation_id: null,
      isBulkLoading: false,
      filter_company_id: "",
      allCompanies: [],
      snackbar: { show: false, text: "", color: "primary" },
      rules: {
        required: (value) => !!value || "Required.",
      },
    };
  },
  computed: {
    geolocationOptions() {
      return this.geolocations.map((g) => ({
        text: `${g.location} (${g.radius || 200}m)`,
        value: g.id,
      }));
    },
    availableLocations() {
      if (!this.selectedUser.geolocations) return this.geolocationOptions;
      const assignedIds = this.selectedUser.geolocations.map((g) => g.id);
      return this.geolocationOptions.filter((g) => !assignedIds.includes(g.value));
    },
    userLocationsForRemoval() {
      if (!this.selectedUser.geolocations || this.selectedUser.geolocations.length === 0) {
        return [];
      }
      const options = this.selectedUser.geolocations.map((g) => ({
        text: g.location,
        value: g.id,
      }));
      if (this.selectedUser.geolocations.length > 1) {
        options.unshift({ text: "Remove All Locations", value: "all" });
      }
      return options;
    },
  },
  mounted() {
    this.getMasters();
    this.getData();
  },
  watch: {
    page() {
      this.getData();
    },
  },
  methods: {
    showSnackbar(text, color) {
      this.snackbar = { show: true, text, color };
    },
    async getMasters() {
      try {
        const response = await axios.get("/user_locations/masters", {
          params: {
            filter_company_id: this.filter_company_id === "all" ? "" : this.filter_company_id || "",
          },
        });
        if (response.data.success) {
          this.geolocations = response.data.geolocations || [];
          if (response.data.companies && response.data.companies.length > 0) {
            this.allCompanies = [{ text: "All Companies", value: "all" }];
            response.data.companies.forEach((c) => {
              this.allCompanies.push({ text: c.name, value: c.id });
            });
          }
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },
    async onCompanyChange() {
      this.filter_geolocation_id = null;
      await this.getMasters();
      this.getData();
    },
    async getData() {
      this.isLoading = true;
      this.selectedUsers = [];
      this.selectAll = false;
      try {
        const response = await axios.get("/user_locations", {
          params: {
            page: this.page,
            rowsPerPage: this.rowsPerPage,
            search_keyword: this.search_keyword,
            geolocation_id: this.filter_geolocation_id,
            without_location: this.without_location ? 1 : 0,
            filter_company_id: this.filter_company_id || "",
          },
        });
        this.users = response.data.data;
        this.paginationLength = Math.ceil(response.data.count / this.rowsPerPage);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        this.isLoading = false;
      }
    },
    openAssignDialog(user) {
      this.selectedUser = { ...user };
      this.assign_geolocation_id = null;
      this.assign_is_primary = false;
      this.assignDialog = true;
    },
    async assignLocation() {
      this.isAssignLoading = true;
      try {
        const payload = {
          user_id: this.selectedUser.id,
          geolocation_id: this.assign_geolocation_id,
          is_primary: this.assign_is_primary,
          is_active: true,
        };
        const response = await axios.post("/user_locations", payload);
        if (response.data.success) {
          this.showSnackbar("Location assigned", "primary");
          this.assignDialog = false;
          this.getData();
        }
      } catch (error) {
        const resp = error.response && error.response.data;
        this.showSnackbar((resp && resp.sub_title) || (resp && resp.message) || "Failed to assign location", "red");
      } finally {
        this.isAssignLoading = false;
      }
    },
    openRemoveDialog(user) {
      this.selectedUser = { ...user };
      this.remove_geolocation_id = null;
      this.removeDialog = true;
    },
    async removeLocation() {
      this.isRemoveLoading = true;
      try {
        let response;
        if (this.remove_geolocation_id === "all") {
          response = await axios.post("/user_locations/remove_all_locations", {
            user_id: this.selectedUser.id,
          });
        } else {
          response = await axios.post("/user_locations/remove_location", {
            user_id: this.selectedUser.id,
            geolocation_id: this.remove_geolocation_id,
          });
        }
        if (response.data.success) {
          this.showSnackbar("Location removed", "deep-orange");
          this.removeDialog = false;
          this.getData();
        }
      } catch (error) {
        const resp = error.response && error.response.data;
        this.showSnackbar((resp && resp.sub_title) || (resp && resp.message) || "Failed to remove location", "red");
      } finally {
        this.isRemoveLoading = false;
      }
    },
    async quickRemove(user, loc) {
      if (!confirm(`Remove "${loc.location}" from ${user.name}?`)) return;
      try {
        await axios.post("/user_locations/remove_location", {
          user_id: user.id,
          geolocation_id: loc.id,
        });
        this.showSnackbar("Location removed", "deep-orange");
        this.getData();
      } catch (error) {
        const resp = error.response && error.response.data;
        this.showSnackbar((resp && resp.sub_title) || "Failed to remove location", "red");
      }
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedUsers = this.users.map((u) => u.id);
      } else {
        this.selectedUsers = [];
      }
    },
    async assignBulkLocations() {
      if (!this.bulk_geolocation_id) return;
      this.isBulkLoading = true;
      try {
        const response = await axios.post("/user_locations/assign_multiple", {
          user_ids: this.selectedUsers,
          geolocation_id: this.bulk_geolocation_id,
        });
        if (response.data.success) {
          this.showSnackbar(response.data.sub_title || "Locations assigned", "primary");
          this.selectedUsers = [];
          this.bulk_geolocation_id = null;
          this.getData();
        }
      } catch (error) {
        const resp = error.response && error.response.data;
        this.showSnackbar((resp && resp.sub_title) || "Failed to assign locations", "red");
      } finally {
        this.isBulkLoading = false;
      }
    },
    async removeBulkLocations() {
      if (!this.bulk_geolocation_id) return;
      if (
        confirm(
          `Are you sure you want to remove the selected location from ${this.selectedUsers.length} users?`
        )
      ) {
        this.isBulkLoading = true;
        try {
          const response = await axios.post("/user_locations/remove_multiple", {
            user_ids: this.selectedUsers,
            geolocation_id: this.bulk_geolocation_id,
          });
          if (response.data.success) {
            this.showSnackbar(response.data.sub_title || "Locations removed", "deep-orange");
            this.selectedUsers = [];
            this.bulk_geolocation_id = null;
            this.getData();
          }
        } catch (error) {
          const resp = error.response && error.response.data;
          this.showSnackbar((resp && resp.sub_title) || "Failed to remove locations", "red");
        } finally {
          this.isBulkLoading = false;
        }
      }
    },
  },
};
</script>
